import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconEdit, IconPlus, IconSearch } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { SortableTh, type SortState, TablePager } from '../purchasing/table.js';
import classes from './PmsLibrary.module.css';
import type { PmsLibrarySource } from './source.js';
import type { LibraryItem, LibraryJob, PmsLibrary } from './types.js';
import {
  CALENDAR_UNITS,
  type CalendarUnit,
  EMPTY_JOB_FILTER,
  filterItems,
  filterJobs,
  hasInterval,
  INTERVAL_BASES,
  intervalLabel,
  type JobFilter,
  jobTypeOf,
  LIBRARY_DEPARTMENTS,
  LIBRARY_WORK_TYPES,
  PAGE_SIZE,
  pageOf,
  pagesOf,
  sortRows,
} from './view.js';

/** Kütüphane hata metni: bilinen kod ya da genel ileti. */
export function useLibraryErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    const detail = (error as { detail?: unknown } | null)?.detail;
    const known = t(`pmsLibrary.errors.${code}`, {
      defaultValue: '',
      detail: typeof detail === 'string' ? detail : '',
    });
    return known || t('pmsLibrary.errors.GENERIC', { code });
  };
}

function summaryText(t: TFunction, page: number, count: number) {
  if (count === 0) return '';
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(count, page * PAGE_SIZE);
  return t('pmsLibrary.showing', { from, to, total: count });
}

/* ---------- Component Library ---------- */

type ItemSortKey = 'code' | 'subSystem' | 'name' | 'jobs';

export function ComponentLibraryTab({
  library,
  canEdit,
  canAdd = false,
  onEdit,
  onAddSystem,
  onAddItem,
  onAddJob,
}: {
  library: PmsLibrary;
  canEdit: boolean;
  /** Elle ekleme (ADR 0102): sistem, sisteme kalem, satırdan kaleme iş. */
  canAdd?: boolean;
  onEdit: (item: LibraryItem) => void;
  onAddSystem?: () => void;
  onAddItem?: (systemId: string | null) => void;
  onAddJob?: (item: LibraryItem) => void;
}) {
  const { t } = useTranslation();
  const [systemId, setSystemId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<ItemSortKey>>({ key: 'code', dir: 'asc' });
  const [page, setPage] = useState(1);
  const fit = useFitColumns<HTMLElement>();
  const rows = useMemo(() => {
    const filtered = filterItems(library, { systemId, query });
    const value = (item: LibraryItem) =>
      sort.key === 'jobs'
        ? item.jobCount
        : sort.key === 'subSystem'
          ? (item.subSystem ?? '')
          : sort.key === 'name'
            ? item.name
            : item.code;
    return sortRows(filtered, value, sort.dir);
  }, [library, systemId, query, sort]);
  const pages = pagesOf(rows.length);
  const shown = pageOf(rows, Math.min(page, pages));
  const k = (key: string) => t(`pmsLibrary.columns.${key}`);
  const actions = canEdit || canAdd;
  return (
    <div className={classes.split}>
      <Panel
        title={t('pmsLibrary.systems')}
        flush
        extra={
          canAdd && onAddSystem ? (
            <Button
              size="compact-sm"
              variant="subtle"
              leftSection={<IconPlus size={14} aria-hidden />}
              onClick={onAddSystem}
            >
              {t('pmsLibrary.actions.addSystem')}
            </Button>
          ) : undefined
        }
      >
        <nav className={classes.systemList} aria-label={t('pmsLibrary.systems')}>
          <button
            type="button"
            className={classes.systemButton}
            data-active={systemId === null || undefined}
            aria-pressed={systemId === null}
            onClick={() => {
              setSystemId(null);
              setPage(1);
            }}
          >
            <span className={classes.systemCode}>—</span>
            <span className={classes.systemName}>{t('pmsLibrary.filters.allSystems')}</span>
            <span className={classes.count}>{library.items.length}</span>
          </button>
          {library.systems.map((system) => (
            <button
              key={system.id}
              type="button"
              className={classes.systemButton}
              data-active={systemId === system.id || undefined}
              aria-pressed={systemId === system.id}
              onClick={() => {
                setSystemId(system.id);
                setPage(1);
              }}
              title={system.name}
            >
              <span className={classes.systemCode}>{system.code}</span>
              <span className={classes.systemName}>{system.name}</span>
              <span className={classes.count}>{system.itemCount}</span>
            </button>
          ))}
        </nav>
      </Panel>
      <Panel title={t('pmsLibrary.itemsTitle')} flush>
        <div className={classes.toolbar}>
          <TextInput
            className={classes.search}
            aria-label={t('pmsLibrary.filters.search')}
            placeholder={t('pmsLibrary.filters.search')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
          />
          {canAdd && onAddItem ? (
            <Button
              className={classes.toolbarAction}
              leftSection={<IconPlus size={16} aria-hidden />}
              onClick={() => onAddItem(systemId)}
            >
              {t('pmsLibrary.actions.addItem')}
            </Button>
          ) : null}
        </div>
        {shown.length === 0 ? (
          <EmptyState title={t('pmsLibrary.empty.items')} />
        ) : (
          <section
            ref={fit}
            className={table.tableWrap}
            aria-label={t('pmsLibrary.itemsList')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.itemsTable ?? ''}`}>
              <thead>
                <tr>
                  <SortableTh
                    column="code"
                    label={k('code')}
                    sort={sort}
                    onSort={setSort}
                    className={classes.colCode ?? ''}
                  />
                  <SortableTh
                    column="subSystem"
                    label={k('subSystem')}
                    sort={sort}
                    onSort={setSort}
                    drop={2}
                  />
                  <SortableTh
                    column="name"
                    label={k('equipment')}
                    sort={sort}
                    onSort={setSort}
                    className={classes.wrapHead ?? ''}
                  />
                  <th scope="col" className={classes.colFlag ?? ''} data-drop={1}>
                    {k('critical')}
                  </th>
                  <SortableTh
                    column="jobs"
                    label={k('jobs')}
                    sort={sort}
                    onSort={setSort}
                    numeric
                    className={classes.colNarrow ?? ''}
                  />
                  <th scope="col" className={classes.colStatus} data-drop={3}>
                    {k('review')}
                  </th>
                  {actions ? (
                    <th scope="col" className={classes.colActions}>
                      <span className={classes.visuallyHidden}>{k('actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => (
                  <tr key={item.id}>
                    <td>{item.code}</td>
                    <td data-drop={2} title={item.subSystem ?? ''}>
                      {item.subSystem ?? '—'}
                    </td>
                    <td title={item.components ?? item.name}>{item.name}</td>
                    <td data-drop={1}>
                      {item.suggestedCritical ? (
                        <StatusBadge tone="danger">{t('pmsLibrary.suggested')}</StatusBadge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td data-numeric>{item.jobCount}</td>
                    <td data-drop={3}>
                      <StatusBadge tone={item.reviewStatus === 'reviewed' ? 'success' : 'neutral'}>
                        {t(`pmsLibrary.statuses.${item.reviewStatus}`)}
                      </StatusBadge>
                    </td>
                    {actions ? (
                      <td className={classes.colActions}>
                        <span className={classes.rowActions}>
                          {canAdd && onAddJob ? (
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              aria-label={t('pmsLibrary.actions.addJobTo', { code: item.code })}
                              title={t('pmsLibrary.actions.addJobTo', { code: item.code })}
                              onClick={() => onAddJob(item)}
                            >
                              <IconPlus size={16} aria-hidden />
                            </Button>
                          ) : null}
                          {canEdit ? (
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              aria-label={`${t('pmsLibrary.actions.edit')} ${item.code}`}
                              onClick={() => onEdit(item)}
                            >
                              <IconEdit size={16} aria-hidden />
                            </Button>
                          ) : null}
                        </span>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <TablePager
          page={Math.min(page, pages)}
          pages={pages}
          summary={summaryText(t, Math.min(page, pages), rows.length)}
          label={t('pmsLibrary.itemsTitle')}
          onPage={setPage}
        />
      </Panel>
    </div>
  );
}

/* ---------- Job Library ---------- */

type JobSortKey = 'code' | 'title' | 'type' | 'interval' | 'status';

export function JobLibraryTab({
  library,
  canEdit,
  canAdd = false,
  onEdit,
  onAddJob,
}: {
  library: PmsLibrary;
  canEdit: boolean;
  /** Elle iş ekleme (ADR 0102): kalem çekmecede seçilir. */
  canAdd?: boolean;
  onEdit: (job: LibraryJob) => void;
  onAddJob?: () => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<JobFilter>(EMPTY_JOB_FILTER);
  const [sort, setSort] = useState<SortState<JobSortKey>>({ key: 'code', dir: 'asc' });
  const [page, setPage] = useState(1);
  const fit = useFitColumns<HTMLElement>();
  const rows = useMemo(() => {
    const filtered = filterJobs(library, filter);
    const value = (job: LibraryJob) =>
      sort.key === 'title'
        ? job.title
        : sort.key === 'type'
          ? job.workType
          : sort.key === 'interval'
            ? job.intervalText
            : sort.key === 'status'
              ? job.status
              : job.code;
    return sortRows(filtered, value, sort.dir);
  }, [library, filter, sort]);
  const pages = pagesOf(rows.length);
  const shown = pageOf(rows, Math.min(page, pages));
  const k = (key: string) => t(`pmsLibrary.columns.${key}`);
  const set = (patch: Partial<JobFilter>) => {
    setFilter((current) => ({ ...current, ...patch }));
    setPage(1);
  };
  return (
    <Panel title={t('pmsLibrary.jobsTitle')} flush>
      <div className={classes.toolbar}>
        <TextInput
          className={classes.search}
          aria-label={t('pmsLibrary.filters.search')}
          placeholder={t('pmsLibrary.filters.search')}
          leftSection={<IconSearch size={16} aria-hidden />}
          value={filter.query}
          onChange={(event) => set({ query: event.currentTarget.value })}
        />
        <Select
          className={classes.filter}
          label={t('pmsLibrary.filters.system')}
          placeholder={t('pmsLibrary.filters.allSystems')}
          clearable
          data={library.systems.map((s) => ({ value: s.code, label: `${s.code} ${s.name}` }))}
          value={filter.systemCode}
          onChange={(value) => set({ systemCode: value })}
        />
        <Select
          className={classes.filter}
          label={t('pmsLibrary.filters.type')}
          clearable
          data={LIBRARY_WORK_TYPES.map((w) => ({
            value: w,
            label: t(`pmsLibrary.workTypes.${w}`),
          }))}
          value={filter.workType}
          onChange={(value) => set({ workType: value })}
        />
        <Select
          className={classes.filter}
          label={t('pmsLibrary.filters.status')}
          clearable
          data={['draft', 'approved'].map((s) => ({
            value: s,
            label: t(`pmsLibrary.statuses.${s}`),
          }))}
          value={filter.status}
          onChange={(value) => set({ status: value })}
        />
        <div>
          <span className={classes.segmentLabel} id="pms-library-interval-filter">
            {t('pmsLibrary.filters.interval')}
          </span>
          <SegmentedControl
            aria-labelledby="pms-library-interval-filter"
            size="xs"
            value={filter.interval}
            onChange={(value) => set({ interval: value as JobFilter['interval'] })}
            data={[
              { value: 'all', label: t('pmsLibrary.filters.intervalAll') },
              { value: 'set', label: t('pmsLibrary.filters.intervalSet') },
              { value: 'missing', label: t('pmsLibrary.filters.intervalMissing') },
            ]}
          />
        </div>
        {canAdd && onAddJob ? (
          <Button
            className={classes.toolbarAction}
            leftSection={<IconPlus size={16} aria-hidden />}
            onClick={onAddJob}
          >
            {t('pmsLibrary.actions.addJob')}
          </Button>
        ) : null}
      </div>
      {shown.length === 0 ? (
        <EmptyState title={t('pmsLibrary.empty.jobs')} />
      ) : (
        <section
          ref={fit}
          className={table.tableWrap}
          aria-label={t('pmsLibrary.jobsList')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.jobsTable ?? ''}`}>
            <thead>
              <tr>
                <SortableTh
                  column="code"
                  label={k('jobCode')}
                  sort={sort}
                  onSort={setSort}
                  className={classes.colJobCode ?? ''}
                />
                <th scope="col" className={classes.colCode} data-drop={2}>
                  {k('item')}
                </th>
                <SortableTh
                  column="title"
                  label={k('title')}
                  sort={sort}
                  onSort={setSort}
                  className={classes.wrapHead ?? ''}
                />
                <SortableTh
                  column="type"
                  label={k('type')}
                  sort={sort}
                  onSort={setSort}
                  className={classes.colFlag ?? ''}
                  drop={3}
                />
                <SortableTh
                  column="interval"
                  label={k('interval')}
                  sort={sort}
                  onSort={setSort}
                  className={classes.colStatus ?? ''}
                />
                <th scope="col" className={classes.colNarrow} data-drop={1}>
                  {k('department')}
                </th>
                <SortableTh
                  column="status"
                  label={k('status')}
                  sort={sort}
                  onSort={setSort}
                  className={classes.colStatus ?? ''}
                  drop={4}
                />
                {canEdit ? (
                  <th scope="col" className={classes.colActions}>
                    <span className={classes.visuallyHidden}>{k('actions')}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {shown.map((job) => (
                <tr key={job.id}>
                  <td>{job.code}</td>
                  <td data-drop={2}>{job.itemCode}</td>
                  <td title={job.title}>{job.title}</td>
                  <td data-drop={3}>
                    {t(`pmsLibrary.workTypes.${job.workType}`, { defaultValue: job.workType })}
                  </td>
                  <td className={hasInterval(job.interval) ? undefined : classes.muted}>
                    {hasInterval(job.interval)
                      ? intervalLabel(job.interval, t)
                      : t('pmsLibrary.noInterval')}
                  </td>
                  <td data-drop={1}>
                    {job.department ? t(`pmsLibrary.departments.${job.department}`) : '—'}
                  </td>
                  <td data-drop={4}>
                    <StatusBadge tone={job.status === 'approved' ? 'success' : 'neutral'}>
                      {t(`pmsLibrary.statuses.${job.status}`)}
                    </StatusBadge>
                  </td>
                  {canEdit ? (
                    <td className={classes.colActions}>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        aria-label={`${t('pmsLibrary.actions.edit')} ${job.code}`}
                        onClick={() => onEdit(job)}
                      >
                        <IconEdit size={16} aria-hidden />
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <TablePager
        page={Math.min(page, pages)}
        pages={pages}
        summary={summaryText(t, Math.min(page, pages), rows.length)}
        label={t('pmsLibrary.jobsTitle')}
        onPage={setPage}
      />
    </Panel>
  );
}

/* ---------- Düzenleme çekmeceleri ---------- */

const text = (value: string | null) => value ?? '';
const blank = (value: string) => (value.trim() === '' ? null : value.trim());

export type ItemFormValues = ReturnType<typeof formOfItem>;
export type JobFormValues = ReturnType<typeof formOfJob>;

/** Kalemin alanları: düzenleme ve elle ekleme aynı formu kullanır (ADR 0063 §5, ADR 0102). */
export function ItemFields({
  values,
  onChange,
}: {
  values: ItemFormValues;
  onChange: (patch: Partial<ItemFormValues>) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pmsLibrary.itemForm.${key}`);
  const field = (key: Exclude<keyof ItemFormValues, 'suggestedCritical'>) => ({
    value: values[key],
    onChange: (event: { currentTarget: { value: string } }) =>
      onChange({ [key]: event.currentTarget.value }),
  });
  return (
    <>
      <TextInput label={k('subSystem')} {...field('subSystem')} />
      <TextInput label={k('name')} required {...field('name')} />
      <Textarea label={k('components')} autosize minRows={2} {...field('components')} />
      <Checkbox
        label={k('suggestedCritical')}
        checked={values.suggestedCritical}
        onChange={(event) => onChange({ suggestedCritical: event.currentTarget.checked })}
      />
      <Group grow>
        <TextInput label={k('maker')} {...field('maker')} />
        <TextInput label={k('model')} {...field('model')} />
      </Group>
      <TextInput label={k('manualRef')} {...field('manualRef')} />
      <Textarea label={k('notes')} autosize minRows={2} {...field('notes')} />
      <Select
        label={k('reviewStatus')}
        allowDeselect={false}
        data={['pending', 'reviewed'].map((s) => ({
          value: s,
          label: t(`pmsLibrary.statuses.${s}`),
        }))}
        value={values.reviewStatus}
        onChange={(value) => onChange({ reviewStatus: value ?? 'pending' })}
      />
    </>
  );
}

/** Kalemin gövdesi; elle eklenen kalemde üretici görev alanları boştur. */
export function itemBodyOf(values: ItemFormValues, item: LibraryItem | null) {
  return {
    subSystem: blank(values.subSystem),
    name: values.name,
    components: blank(values.components),
    suggestedCritical: values.suggestedCritical,
    notes: blank(values.notes),
    maker: blank(values.maker),
    model: blank(values.model),
    makerTaskRef: item?.makerTaskRef ?? null,
    makerTask: item?.makerTask ?? null,
    makerInterval: item?.makerInterval ?? null,
    manualRef: blank(values.manualRef),
    taskBasis: item?.taskBasis ?? null,
    reviewStatus: values.reviewStatus,
  };
}

/** İşin alanları: aralık, dayanak, tür, departman, kritik, üretici alanları, durum. */
export function JobFields({
  values,
  onChange,
}: {
  values: JobFormValues;
  onChange: (patch: Partial<JobFormValues>) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pmsLibrary.jobForm.${key}`);
  const textField = (key: 'title' | 'makerRecommendation' | 'makerInterval' | 'manualRef') => ({
    value: values[key],
    onChange: (event: { currentTarget: { value: string } }) =>
      onChange({ [key]: event.currentTarget.value }),
  });
  return (
    <>
      <Textarea label={k('titleField')} required autosize minRows={2} {...textField('title')} />
      <Select
        label={k('workType')}
        allowDeselect={false}
        data={LIBRARY_WORK_TYPES.map((w) => ({
          value: w,
          label: t(`pmsLibrary.workTypes.${w}`),
        }))}
        value={values.workType}
        onChange={(value) => onChange({ workType: value ?? 'Inspection' })}
      />
      <fieldset className={classes.wizardRow} style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className={classes.muted}>{k('interval')}</legend>
        <NumberInput
          label={k('every')}
          min={1}
          max={1000}
          allowDecimal={false}
          value={values.every}
          onChange={(value) => onChange({ every: value === '' ? '' : Number(value) })}
        />
        <Select
          label={k('unit')}
          allowDeselect={false}
          data={CALENDAR_UNITS.map((u) => ({
            value: u,
            label: t(`pmsLibrary.unitNames.${u}`),
          }))}
          value={values.unit}
          onChange={(value) => onChange({ unit: (value ?? 'month') as CalendarUnit })}
        />
      </fieldset>
      <NumberInput
        label={k('hours')}
        min={1}
        max={1_000_000}
        decimalScale={1}
        value={values.hours}
        onChange={(value) => onChange({ hours: value === '' ? '' : Number(value) })}
      />
      <Select
        label={k('basis')}
        clearable
        data={INTERVAL_BASES.map((b) => ({ value: b, label: t(`pmsLibrary.bases.${b}`) }))}
        value={values.basis || null}
        onChange={(value) => onChange({ basis: value ?? '' })}
      />
      <Select
        label={k('department')}
        clearable
        placeholder={t('pmsLibrary.unassigned')}
        data={LIBRARY_DEPARTMENTS.map((d) => ({
          value: d,
          label: t(`pmsLibrary.departments.${d}`),
        }))}
        value={values.department || null}
        onChange={(value) => onChange({ department: value ?? '' })}
      />
      <Checkbox
        label={k('critical')}
        checked={values.critical}
        onChange={(event) => onChange({ critical: event.currentTarget.checked })}
      />
      <TextInput label={k('makerRecommendation')} {...textField('makerRecommendation')} />
      <TextInput label={k('makerInterval')} {...textField('makerInterval')} />
      <TextInput label={k('manualRef')} {...textField('manualRef')} />
      <Select
        label={k('status')}
        description={k('statusHint')}
        allowDeselect={false}
        data={['draft', 'approved'].map((s) => ({
          value: s,
          label: t(`pmsLibrary.statuses.${s}`),
        }))}
        value={values.status}
        onChange={(value) => onChange({ status: value ?? 'draft' })}
      />
    </>
  );
}

/** İşin gövdesi; elle eklenen işte planlama metni boştur. */
export function jobBodyOf(values: JobFormValues, job: LibraryJob | null) {
  return {
    title: values.title,
    workType: values.workType,
    jobType: jobTypeOf(values.workType),
    interval: {
      calendar: values.every === '' ? null : { every: values.every, unit: values.unit },
      runningHours: values.hours === '' ? null : values.hours,
    },
    intervalBasis: values.basis || null,
    planningBasis: job?.planningBasis ?? null,
    makerRecommendation: blank(values.makerRecommendation),
    makerInterval: blank(values.makerInterval),
    manualRef: blank(values.manualRef),
    department: values.department || null,
    critical: values.critical,
    status: values.status,
  };
}

export function ItemDrawer({
  item,
  source,
  canAdd = false,
  onClose,
  onSaved,
  onAddJob,
}: {
  item: LibraryItem | null;
  source: PmsLibrarySource;
  /** Ayrıntıdan "Add Job" (ADR 0102). */
  canAdd?: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onAddJob?: (item: LibraryItem) => void;
}) {
  const { t } = useTranslation();
  const errorText = useLibraryErrorText();
  const [values, setValues] = useState(() => formOfItem(item));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    setValues(formOfItem(item));
    setError(null);
    setConfirm(false);
  }, [item]);
  const k = (key: string, v: Record<string, unknown> = {}) => t(`pmsLibrary.itemForm.${key}`, v);
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onSaved();
      onClose();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      opened={item !== null}
      onClose={onClose}
      position="right"
      size="md"
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('pmsLibrary.actions.close') }}
    >
      {item ? (
        <Stack gap="sm">
          <Group align="end" wrap="nowrap">
            <TextInput
              className={classes.grow}
              label={t('pmsLibrary.columns.code')}
              value={item.code}
              readOnly
            />
            {canAdd && onAddJob ? (
              <Button
                variant="light"
                leftSection={<IconPlus size={16} aria-hidden />}
                onClick={() => onAddJob(item)}
              >
                {t('pmsLibrary.actions.addJob')}
              </Button>
            ) : null}
          </Group>
          <ItemFields
            values={values}
            onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
          />
          {error ? <Alert color="red">{error}</Alert> : null}
          {confirm ? (
            <Alert color="red" title={k('removeTitle')}>
              {k('removeText', { count: item.jobCount })}
              <Group mt="sm">
                <Button
                  color="red"
                  size="xs"
                  loading={busy}
                  onClick={() => run(() => source.removeItem(item.id, item.rowVersion))}
                >
                  {t('pmsLibrary.actions.remove')}
                </Button>
                <Button size="xs" variant="default" onClick={() => setConfirm(false)}>
                  {t('pmsLibrary.actions.cancel')}
                </Button>
              </Group>
            </Alert>
          ) : null}
          <Group justify="space-between">
            <Button variant="subtle" color="red" onClick={() => setConfirm(true)}>
              {t('pmsLibrary.actions.remove')}
            </Button>
            <Group>
              <Button variant="default" onClick={onClose}>
                {t('pmsLibrary.actions.cancel')}
              </Button>
              <Button
                loading={busy}
                onClick={() =>
                  run(() =>
                    source.updateItem(item.id, {
                      rowVersion: item.rowVersion,
                      ...itemBodyOf(values, item),
                    }),
                  )
                }
              >
                {t('pmsLibrary.actions.save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      ) : null}
    </Drawer>
  );
}

export function formOfItem(item: LibraryItem | null) {
  return {
    subSystem: text(item?.subSystem ?? null),
    name: item?.name ?? '',
    components: text(item?.components ?? null),
    suggestedCritical: item?.suggestedCritical ?? false,
    notes: text(item?.notes ?? null),
    maker: text(item?.maker ?? null),
    model: text(item?.model ?? null),
    manualRef: text(item?.manualRef ?? null),
    reviewStatus: item?.reviewStatus ?? 'pending',
  };
}

export function formOfJob(job: LibraryJob | null) {
  return {
    title: job?.title ?? '',
    workType: job?.workType ?? 'Inspection',
    every: (job?.interval.calendar?.every ?? '') as number | '',
    unit: (job?.interval.calendar?.unit ?? 'month') as CalendarUnit,
    hours: (job?.interval.runningHours ?? '') as number | '',
    basis: job?.intervalBasis ?? '',
    department: job?.department ?? '',
    critical: job?.critical ?? false,
    makerRecommendation: text(job?.makerRecommendation ?? null),
    makerInterval: text(job?.makerInterval ?? null),
    manualRef: text(job?.manualRef ?? null),
    status: job?.status ?? 'draft',
  };
}

/** İşin aralığını ve durumunu düzenler: "aralıkları biz seçeceğiz" (ADR 0063 §5). */
export function JobDrawer({
  job,
  source,
  onClose,
  onSaved,
}: {
  job: LibraryJob | null;
  source: PmsLibrarySource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useLibraryErrorText();
  const [values, setValues] = useState(() => formOfJob(job));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    setValues(formOfJob(job));
    setError(null);
    setConfirm(false);
  }, [job]);
  const k = (key: string) => t(`pmsLibrary.jobForm.${key}`);
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onSaved();
      onClose();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      opened={job !== null}
      onClose={onClose}
      position="right"
      size="md"
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('pmsLibrary.actions.close') }}
    >
      {job ? (
        <Stack gap="sm">
          <TextInput
            label={t('pmsLibrary.columns.jobCode')}
            value={`${job.code} · ${job.itemCode}`}
            readOnly
          />
          <JobFields values={values} onChange={(patch) => setValues((v) => ({ ...v, ...patch }))} />
          {error ? <Alert color="red">{error}</Alert> : null}
          {confirm ? (
            <Alert color="red" title={k('removeTitle')}>
              {k('removeText')}
              <Group mt="sm">
                <Button
                  color="red"
                  size="xs"
                  loading={busy}
                  onClick={() => run(() => source.removeJob(job.id, job.rowVersion))}
                >
                  {t('pmsLibrary.actions.remove')}
                </Button>
                <Button size="xs" variant="default" onClick={() => setConfirm(false)}>
                  {t('pmsLibrary.actions.cancel')}
                </Button>
              </Group>
            </Alert>
          ) : null}
          <Group justify="space-between">
            <Button variant="subtle" color="red" onClick={() => setConfirm(true)}>
              {t('pmsLibrary.actions.remove')}
            </Button>
            <Group>
              <Button variant="default" onClick={onClose}>
                {t('pmsLibrary.actions.cancel')}
              </Button>
              <Button
                loading={busy}
                onClick={() =>
                  run(() =>
                    source.updateJob(job.id, {
                      rowVersion: job.rowVersion,
                      ...jobBodyOf(values, job),
                    }),
                  )
                }
              >
                {t('pmsLibrary.actions.save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      ) : null}
    </Drawer>
  );
}
